// Commander PSIM — i18next configuration
//
// Loads per-namespace JSON from `/locales/{lng}/{ns}.json` (served by
// nginx from `apps/web/public/locales/`). Each module gets its own
// namespace so a per-module translation PR is a self-contained drop.
//
// Language detection order:
//   1. localStorage key `commander-language` (set by ui.store +
//      synchronous boot script in main.tsx — keeps first-paint in
//      the right direction without a flash)
//   2. `navigator.language` — only used on fresh installs
// Fallback: 'en'.
//
// The actual <html dir>/<html lang> mutation happens in three places:
//   - main.tsx boot script (synchronous, before React mounts)
//   - ui.store.setLanguage (when the operator picks one)
//   - i18n.on('languageChanged') listener below (catch-all)
// All three keep the DOM and the i18next instance in sync.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';

// Injected by Vite (see vite.config.ts `define`).
declare const __BUILD_ID__: string;
import LanguageDetector from 'i18next-browser-languagedetector';

export const SUPPORTED_LANGUAGES = ['en', 'he'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LOCALSTORAGE_KEY = 'commander-language';

/** Languages whose script reads right-to-left. Used by the boot
 *  script and the languageChanged listener to set <html dir>. */
const RTL_LANGUAGES: ReadonlySet<string> = new Set(['he', 'ar', 'fa', 'ur']);

export function isRtlLanguage(lng: string | undefined | null): boolean {
  if (!lng) return false;
  const base = lng.split('-')[0].toLowerCase();
  return RTL_LANGUAGES.has(base);
}

/** Apply `<html dir>` and `<html lang>` for a given language. Safe to
 *  call before or after React mounts — purely DOM. */
export function applyDirAndLang(lng: string): void {
  if (typeof document === 'undefined') return;
  const base = lng.split('-')[0].toLowerCase();
  document.documentElement.lang = base;
  document.documentElement.dir = isRtlLanguage(base) ? 'rtl' : 'ltr';
}

// Namespaces live one-per-module so Phase 3 lands by module. Phase 1
// only populates `common`, `nav`, `auth`, `profile`. The others are
// declared up-front so a code path that calls t('cameras:foo') won't
// fail before the file exists — i18next just renders the key.
const namespaces = [
  'common',
  'nav',
  'auth',
  'profile',
  'incidents',
  'operator',
  'cameras',
  'maps',
  'floorplan',
  'events',
  'devices',
  'dashboard',
  'hmi',
  'reports',
  'configuration',
  'settings',
  'admin',
  'misc',
  'errors',
  'pages',
  'components',
  'vendor',
] as const;

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true, // 'he-IL' → 'he'
    ns: namespaces as unknown as string[],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false, // React escapes for us
    },
    backend: {
      // Cache-bust per build. This path is NOT content-hashed the way the JS/CSS
      // bundles are, so without a version query the browser keeps serving the
      // translation file it cached on a previous visit — new keys then render as
      // raw key paths ("addDeviceModal.pick.driverCount") even though the server
      // has them. BUILD_ID changes every build, so each deploy fetches fresh.
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${__BUILD_ID__}`,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_LOCALSTORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      // Missing-key behavior — show the key path so we notice when a
      // file or entry hasn't been added yet. In production this'll be
      // visible to the operator; in Phase 3 every key gets backfilled.
      useSuspense: false,
    },
  });

// Catch-all: even if someone changes the language outside the store,
// the <html dir>/<html lang> attributes stay in sync.
i18n.on('languageChanged', (lng) => {
  applyDirAndLang(lng);
});

export default i18n;
